import { config } from 'src/config'
import { getPostCanonicals } from 'src/data/mappers/post/getPostCanonicals'
import { getPostSlug } from 'src/data/mappers/post/getPostSlug'
import { getPostType } from 'src/data/mappers/post/getPostType'
import { getUnicodeLocale } from 'src/functions/i18n/localeFns'

import type { IsoLocale } from 'src/enum/IsoLocale'
import type { Locale } from 'src/enum/Locale'
import type { Author } from 'src/types/Author'
import type { CmsCanonical } from 'src/types/Canonical'
import type { Image } from 'src/types/Image'
import type {
  AccordionModule,
  ColumnsModule,
  GalleryModule,
  HeadlineModule,
  HtmlModule,
  ImageModule,
  InfoboxModule,
  Module,
  OrganismModule,
  PlaceholderModule,
  ProductGridModule,
  TextblockModule,
  VideoModule,
} from 'src/types/Module'
import type { Post, PostBrewGuide, PostHeader, PostSeo, PostSharing, PostType } from 'src/types/Post'

export const mapBlogPost = (rawPost: any, locale: Locale): Post => ({
  id: rawPost.id,
  type: getPostType(rawPost.type),
  title: rawPost.title.rendered, // unescape blog title
  excerpt: rawPost.acf.excerpt || null,
  slug: getPostSlug(rawPost),
  date: rawPost.date,
  translations: rawPost.translations,
  author: mapAuthor(rawPost),
  category: rawPost.acf['data-category'] || null,
  locale: rawPost.language ? getUnicodeLocale(rawPost.language) : (config.defaultLocales.canonical as Locale),
  seo: mapSeo(rawPost, locale),
  sharing: mapSharing(rawPost),
  header: mapHeader(rawPost),
  brewguide: mapBrewGuide(rawPost),
  modules_and_components: mapModulesAndComponents(rawPost.acf.modules_and_components),
})

const getImageIdentifier = (imageUrl: string | null): string | null => {
  if (!imageUrl) return null
  const cdnUrl = imageUrl.replace('ucarecdn.com', 'cdn.coffeecircle.com')
  const idMatch = cdnUrl.match(/.com\/([\w-]*)/)
  return cdnUrl.includes('cdn.coffeecircle.com') && idMatch ? idMatch[1] : null
}

const mapImage = (image: any): Image => ({
  id: getImageIdentifier(image.url) || config.imagePlaceholderUploadcareId,
  filename: image.filename,
  name: image.name,
  alt: image.alt,
  title: image.title,
  caption: image.caption,
  width: image.width,
  height: image.height,
})

const mapAuthor = (rawPost: any): Author => {
  const authorList = rawPost.acf['data-author-userlist'] || {}
  const customName = rawPost.acf['data-author-manual']
  const imageId = customName ? null : getImageIdentifier(authorList.user_url)

  return {
    name: customName || authorList.display_name || '',
    description: authorList.user_description || '',
    imageId,
  }
}

const mapSeo = (rawPost: any, locale: Locale): PostSeo => ({
  metaDescription: rawPost.acf['data-metadescription'] || null,
  langHrefs: rawPost['lang-hrefs'],
  canonicals: buildSeoCanonicals(rawPost['lang-hrefs'], locale, rawPost.type),
  noIndex: Boolean(rawPost.acf['data-noindex']),
})

const buildSeoCanonicals = (langHrefs: PostSeo['langHrefs'], locale: Locale, postType: PostType): CmsCanonical[] => {
  type LocaleSlug = { [key in Locale]?: string }
  const translationSlugs: LocaleSlug = {}

  Object.entries(langHrefs).forEach(([isoLocale, slug]) => {
    translationSlugs[getUnicodeLocale(isoLocale as IsoLocale)] = slug
  })

  return getPostCanonicals(translationSlugs, locale, postType)
}

const mapSharing = (rawPost: any): PostSharing | null => {
  if (rawPost.acf['data-sharing-options']) {
    return {
      title: rawPost.acf['data-sharingtitle'],
      image: rawPost.acf['data-sharingimage'] ? mapImage(rawPost.acf['data-sharingimage']) : null,
    }
  }
  return null
}

const mapHeader = (rawPost: any): PostHeader => ({
  image: rawPost.acf['data-headerimage'] ? mapImage(rawPost.acf['data-headerimage']) : null,
  isSimple: Boolean(rawPost.acf['data-simpleheader']),
  showTitle: Boolean(rawPost.acf['data-showtitle']),
  opacity: Number(rawPost.acf['data-opacity']) || null,
  position: rawPost.acf['data-position'] || null,
})

const mapBrewGuide = (rawPost: any): PostBrewGuide | null => {
  if (rawPost.acf['data-brewguide']) {
    return {
      weight: rawPost.acf['data-weight'],
      coarseness: rawPost.acf['data-coarseness'],
      waterAmount: rawPost.acf['data-wateramount'],
      temperature: rawPost.acf['data-temperature'],
      duration: rawPost.acf['data-duration'],
      durationAmount: rawPost.acf['data-duration-amount'],
      recipeItems: rawPost.acf['data-items'],
    }
  }
  return null
}

const mapModulesAndComponents = (modulesAndComponents: any[]): Module[] => {
  const result: Module[] = []

  if (!modulesAndComponents) return result

  modulesAndComponents.forEach((module: any) => {
    switch (module.acf_fc_layout) {
      case 'accordion': {
        result.push({
          acf_fc_layout: module.acf_fc_layout,
          contents: module.contents.map((item: any) => ({
            title: item['accordion-title'],
            content: item['accordion-content'],
          })),
          style: module['data-style'],
        } as AccordionModule as Module)
        break
      }

      case 'columns': {
        const mapped = {
          acf_fc_layout: module.acf_fc_layout,
          column: module.column.map((c: any) => ({
            column_module: { modules_and_components: mapModulesAndComponents(c.column_module.modules_and_components) },
            column_width: c.column_width,
          })),
        } as ColumnsModule as Module

        if (module['data-bgcolor'] !== 'light') mapped.bgColor = module['data-bgcolor']
        if (module['data-bgimage']) mapped.bgImage = mapImage(module['data-bgimage'])
        if (module['data-bgimage-opacity'] !== '1') mapped.bgImageOpacity = Number(module['data-bgimage-opacity'])
        if (module['data-fullwidth']) mapped.fullWidth = module['data-fullwidth']
        if (module['data-wrapcontent']) mapped.wrapContent = module['data-wrapcontent']
        result.push(mapped)
        break
      }

      case 'gallery': {
        const rawImages = module.images || []
        const images = rawImages.map((image: any) => mapImage(image))
        const mapped = {
          acf_fc_layout: module.acf_fc_layout,
          type: module['data-gallery'],
          images,
        } as GalleryModule as Module

        if (mapped.type === 'grid') mapped.layout = module['data-layout'] || null
        result.push(mapped)
        break
      }

      case 'headline': {
        const mapped = {
          acf_fc_layout: module.acf_fc_layout,
          headline: module.headline,
          size: module['data-size'],
        } as HeadlineModule as Module

        if (module.subline) mapped.subline = module.subline
        if (module['data-alignment'] !== 'alignleft') mapped.alignment = module['data-alignment']
        if (module['data-inline-nav'] && module['data-inline-nav-altname'])
          mapped.anchor = module['data-inline-nav-altname']
        if (module['data-divider']) mapped.divider = module['data-divider']
        result.push(mapped)
        break
      }

      case 'html': {
        const mapped = {
          acf_fc_layout: module.acf_fc_layout,
          content: module.content,
        } as HtmlModule as Module

        if (module['data-fullwidth']) mapped.fullWidth = module['data-fullwidth']
        result.push(mapped)
        break
      }

      case 'image': {
        const mapped = {
          acf_fc_layout: module.acf_fc_layout,
          image: module.image?.id ? mapImage(module.image) : null,
        } as ImageModule as Module

        if (module['data-fullwidth']) mapped.fullWidth = module['data-fullwidth']
        if (module['data-gradient-overlay']) mapped.gradient = module['data-gradient-overlay']
        if (module['data-link']) mapped.link = module['data-link']
        if (module['data-linkdescription']) mapped.linkDescription = module['data-linkdescription']
        if (module['data-showcaption']) mapped.showCaption = module['data-showcaption']
        if (module['data-theme'] === 'dark') mapped.theme = module['data-theme']
        if (module['data-title']) mapped.title = module['data-title']
        if (module['data-opacity'] !== '1') mapped.opacity = Number(module['data-opacity'])
        if (module['data-overlay']) mapped.overlay = module['data-overlay']
        result.push(mapped)
        break
      }

      case 'infobox': {
        result.push({
          acf_fc_layout: module.acf_fc_layout,
          headline: module.headline,
          text: module.text,
          bgColor: module['data-bgcolor'],
        } as InfoboxModule as unknown as Module)
        break
      }

      case 'organism': {
        result.push({
          acf_fc_layout: module.acf_fc_layout,
          organism: module['data-organism'],
          subOrganismNewsletter: module['data-suborganism-newsletter'],
        } as OrganismModule as Module)
        break
      }

      case 'placeholder': {
        const mapped = { acf_fc_layout: module.acf_fc_layout } as PlaceholderModule as Module

        if (module['data-bgcolor']) mapped.bgColor = module['data-bgcolor']
        if (module['data-fullwidth']) mapped.fullWidth = module['data-fullwidth']
        if (module.height) mapped.height = module.height
        result.push(mapped)
        break
      }

      case 'product-grid': {
        result.push({
          acf_fc_layout: module.acf_fc_layout,
          products: module.products || [],
          skus: module.skus || [],
        } as ProductGridModule as Module)
        break
      }

      case 'textblock': {
        const mapped = {
          acf_fc_layout: module.acf_fc_layout,
          text: module.text,
        } as TextblockModule as Module

        if (module['data-centered']) mapped.centered = module['data-centered']
        if (module['data-dropcap']) mapped.dropcap = module['data-dropcap']
        if (module['data-introtext']) mapped.intro = module['data-introtext']
        result.push(mapped)
        break
      }

      case 'video': {
        const mapped = {
          acf_fc_layout: module.acf_fc_layout,
          video: module.video,
        } as VideoModule as Module

        if (module['data-fullwidth']) mapped.fullWidth = module['data-fullwidth']
        result.push(mapped)
        break
      }

      default: {
        result.push(module)
      }
    }
  })

  return result
}
